import { Box, Paper, Typography } from "@mui/material";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { PaymentGateway } from "../../../../create/PaymentMethods";
import { colors, alphaVar } from "../../../../../ui/colors";
import { ManualPaymentInstructions } from "./ManualPaymentInstruccions";
import { OnlinePaymentButton } from "./OnlinePaymentButton";

interface BankDetails {
  accountNumber?: string;
  routingNumber?: string;
  bankName?: string;
  accountHolder?: string;
  swift?: string;
  iban?: string;
}

interface PaymentMethodPreviewProps {
  selectedGateways: PaymentGateway[];
  totalAmount?: number;
  paymentAmount?: number;
  showPreviewWrapper?: boolean;
  onPaymentClick?: (selectedGatewayId: string) => void;
  buttonDisabled?: boolean;
  paymentMethods?: any;
  bankDetails?: BankDetails;
  referenceNumber?: string;
  handleChangeStatus?: any;
  onPayPalCaptureSuccess?: () => void;
  invoiceUid?: string;
  invoiceType?: string;
}

export const PaymentMethodPreview = ({
  selectedGateways,
  totalAmount = 3726.0,
  paymentAmount,
  paymentMethods,
  handleChangeStatus,
  onPayPalCaptureSuccess,
  showPreviewWrapper = true,
  onPaymentClick,
  buttonDisabled = false,
  bankDetails,
  referenceNumber,
  invoiceUid,
  invoiceType,
}: PaymentMethodPreviewProps) => {
  const [previewSelectedId, setPreviewSelectedId] = useState<string>(null);
  const [showPaymentMethod, setShowPaymentMethod] = useState(false);

  const getGatewayIcon = (iconName: string) => {
    switch (iconName) {
      case "payments":
        return <PaymentsIcon sx={{ fontSize: 18, color: "white" }} />;
      case "account_balance_wallet":
        return <AccountBalanceWalletIcon sx={{ fontSize: 18, color: "white" }} />;
      default:
        return <PaymentsIcon sx={{ fontSize: 18, color: "white" }} />;
    }
  };

  const getGatewayDisplayName = (gateway: PaymentGateway) => {
    return gateway.displayName;
  };

  const handlePaymentMethodSelect = (gatewayId: string) => {
    setPreviewSelectedId(gatewayId);
    setTimeout(() => {
      setShowPaymentMethod(true);
    }, 300);
  };

  const handleBackToMethods = () => {
    setShowPaymentMethod(false);
  };

  const handlePayment = () => {
    if (onPaymentClick && previewSelectedId) {
      onPaymentClick(previewSelectedId);
    }
  };

  if (selectedGateways.length === 0) {
    return null;
  }

  if (selectedGateways.length === 1) {
    const singleGateway = selectedGateways[0];

    return (
      <Box sx={{ width: "100%" }}>
        {showPreviewWrapper && (
          <>
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: colors.gray[400],
                mb: 2,
                display: "block",
              }}
            >
              Customer Preview
            </Typography>
          </>
        )}

        {singleGateway.id === "bank_transfer" ? (
          <ManualPaymentInstructions bankDetails={bankDetails} referenceNumber={referenceNumber} />
        ) : (
          <OnlinePaymentButton
            handleChangeStatus={handleChangeStatus}
            onPayPalCaptureSuccess={onPayPalCaptureSuccess}
            paymentMethods={paymentMethods}
            gatewayId={singleGateway.id}
            displayName={getGatewayDisplayName(singleGateway)}
            totalAmount={totalAmount}
            paymentAmount={paymentAmount}
            invoiceUid={invoiceUid}
            invoiceType={invoiceType}
            onPaymentClick={handlePayment}
            buttonDisabled={buttonDisabled}
          />
        )}
      </Box>
    );
  }

  const paymentMethodsContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {selectedGateways.map((gateway) => {
        const isSelected = previewSelectedId === gateway.id;
        return (
          <Box
            key={gateway.id}
            onClick={() => handlePaymentMethodSelect(gateway.id)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1.5,
              bgcolor: isSelected ? alphaVar(colors.orange.base, 0.05) : "background.paper",
              border: `1px solid ${isSelected ? colors.orange.base : alphaVar(colors.gray[200], 0.5)}`,
              borderRadius: 2,
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: colors.orange.base,
                bgcolor: alphaVar(colors.orange.base, 0.04),
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: gateway.iconColor || colors.orange.base,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {getGatewayIcon(gateway.iconName)}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  color: colors.gray[600],
                }}
              >
                {getGatewayDisplayName(gateway)}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                bgcolor: isSelected ? colors.orange.base : "transparent",
                border: `2px solid ${isSelected ? colors.orange.base : colors.gray[300]}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isSelected && (
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  }}
                />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  const selectedGateway = selectedGateways.find((g) => g.id === previewSelectedId);

  const paymentMethodContent = selectedGateway && (
    <Box>
      {selectedGateway.id === "bank_transfer" ? (
        <ManualPaymentInstructions bankDetails={bankDetails} referenceNumber={referenceNumber} onBackClick={handleBackToMethods} />
      ) : (
        <OnlinePaymentButton
          handleChangeStatus={handleChangeStatus}
          onPayPalCaptureSuccess={onPayPalCaptureSuccess}
          paymentMethods={paymentMethods}
          gatewayId={selectedGateway.id}
          displayName={getGatewayDisplayName(selectedGateway)}
          totalAmount={totalAmount}
          paymentAmount={paymentAmount}
          invoiceUid={invoiceUid}
          invoiceType={invoiceType}
          onPaymentClick={handlePayment}
          buttonDisabled={buttonDisabled}
          onBackClick={handleBackToMethods}
        />
      )}
    </Box>
  );

  return (
    <Box sx={{ width: "100%", maxWidth: 420, minWidth: 0 }}>
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ mb: 1.5, color: colors.gray[500], display: "block", textTransform: "uppercase", letterSpacing: "0.08em" }}
      >
        Select payment method
      </Typography>

      <AnimatePresence mode="wait">
        {!showPaymentMethod && (
          <motion.div
            key="methods-list"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {paymentMethodsContent}
          </motion.div>
        )}

        {showPaymentMethod && (
          <motion.div
            key="payment-method"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {paymentMethodContent}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};
